/**
 * CartDrawerComplementary
 *
 * Fetches complementary products for items currently in the cart using
 * Shopify's Product Recommendations API (?intent=complementary) and
 * injects them into the cart drawer.
 *
 * Uses a module-level cache so that when the cart drawer replaces its
 * innerHTML (destroying and recreating this element), the cached
 * recommendations are shown instantly while a background refresh runs.
 */

(() => {
  /* ── Module-level cache (survives element destroy/recreate) ──── */
  const _cache = {
    html: '',           // cached innerHTML of the container
    cartItemIds: '',    // stringified product IDs that produced this cache
  };

  class CartDrawerComplementary extends HTMLElement {
    connectedCallback() {
      this._container = this.querySelector('[data-complementary-container]');
      this._wrapper   = this.closest('.cart-drawer__recommendations');
      if (!this._container) return;

      // 1. Instantly restore cached content (no flash)
      if (_cache.html) {
        this._container.innerHTML = _cache.html;
        if (this._wrapper) this._wrapper.hidden = false;
      }

      // 2. Refresh in background
      this._abortController = new AbortController();
      this._fetchRecommendations();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    async _fetchRecommendations() {
      const sectionId = this.getAttribute('section-id');
      const limit     = parseInt(this.getAttribute('limit') || '6', 10);
      const intent    = this.getAttribute('intent') || 'complementary';
      const signal    = this._abortController.signal;

      /* ── 1. Get current cart ─────────────────────────────────── */
      let cart;
      try {
        cart = await (await fetch(`${Shopify.routes.root}cart.js`, { signal })).json();
      } catch (_) {
        return;
      }

      if (!cart.items.length) {
        if (this._wrapper) this._wrapper.hidden = true;
        _cache.html = '';
        _cache.cartItemIds = '';
        return;
      }

      /* ── 2. Check if cart products changed since last fetch ──── */
      const uniqueProductIds = [...new Set(cart.items.map(i => i.product_id))];
      const cartKey = uniqueProductIds.slice().sort().join(',');

      // If cart products haven't changed, cached content is still valid — skip fetch
      if (cartKey === _cache.cartItemIds && _cache.html) {
        return;
      }

      /* ── 3. Fetch complementary products (up to 3 requests) ──── */
      const seenIds     = new Set();
      const allElements = [];

      const fetches = uniqueProductIds.slice(0, 3).map(async (productId) => {
        const url = `${Shopify.routes.root}recommendations/products`
          + `?product_id=${productId}`
          + `&limit=${limit}`
          + `&section_id=${sectionId}`
          + `&intent=${intent}`;

        try {
          const html    = await (await fetch(url, { signal })).text();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          tempDiv.querySelectorAll('[data-complementary-product-id]').forEach(item => {
            const pid = item.getAttribute('data-complementary-product-id');
            if (!seenIds.has(pid)) {
              seenIds.add(pid);
              const card = item.querySelector('.horizontal-product');
              if (card) allElements.push(card);
            }
          });
        } catch (e) {
          if (e.name !== 'AbortError') {
            console.error('[CartDrawerComplementary] fetch error:', e);
          }
        }
      });

      await Promise.all(fetches);

      // Bail if element was disconnected during fetch
      if (signal.aborted) return;

      /* ── 4. Render & update cache ────────────────────────────── */
      if (allElements.length > 0) {
        this._container.innerHTML = '';
        allElements.slice(0, limit).forEach(el => {
          this._container.appendChild(el.cloneNode(true));
        });

        if (this._wrapper) this._wrapper.hidden = false;

        // Update cache
        _cache.html = this._container.innerHTML;
        _cache.cartItemIds = cartKey;

        // Re-init payment buttons
        if (window.Shopify?.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        if (this._wrapper) this._wrapper.hidden = true;
        _cache.html = '';
        _cache.cartItemIds = cartKey;
      }
    }
  }

  if (!window.customElements.get('cart-drawer-complementary')) {
    window.customElements.define('cart-drawer-complementary', CartDrawerComplementary);
  }
})();